import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const pad = n => String(n).padStart(2, "0");
export const isoOf = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

export function countWorkingDays(from, to, hols) {
  if (!from || !to) return 0;
  let n = 0;
  for (let d = new Date(from + "T00:00:00"); d <= new Date(to + "T00:00:00"); d.setDate(d.getDate() + 1)) {
    const iso = isoOf(d.getFullYear(), d.getMonth(), d.getDate());
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6 && !hols[iso]) n++;
  }
  return n;
}

function cells(y, m) {
  const first = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  return [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
}

// Month view: tap a FROM date then a TO date; range is highlighted. Holidays coloured with name on tap/hover.
export function LeaveCalendar({ from, to, onChange, hols }) {
  const start = from ? new Date(from + "T00:00:00") : new Date();
  const [ym, setYm] = useState({ y: start.getFullYear(), m: start.getMonth() });
  const [tip, setTip] = useState("");
  const touch = useRef(null);
  const shift = k => setYm(({ y, m }) => { const d = new Date(y, m + k, 1); return { y: d.getFullYear(), m: d.getMonth() }; });

  function tap(iso) {
    setTip(hols[iso] ? `${hols[iso]} — public holiday, not counted` : "");
    if (!from || (from && to) || iso < from) onChange(iso, "");
    else onChange(from, iso);
  }

  const label = new Date(ym.y, ym.m, 1).toLocaleDateString("en-NZ", { month: "long", year: "numeric" });
  return (
    <div className="card !p-3 select-none" data-testid="leave-calendar"
      onTouchStart={e => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={e => { const dx = e.changedTouches[0].clientX - (touch.current ?? 0); if (Math.abs(dx) > 50) shift(dx < 0 ? 1 : -1); }}>
      <div className="flex items-center justify-between mb-2">
        <button type="button" className="btn-ghost px-3 py-2" onClick={() => shift(-1)} aria-label="Previous month" data-testid="cal-prev"><ChevronLeft size={18} /></button>
        <div className="text-silver font-medium" data-testid="cal-month">{label}</div>
        <button type="button" className="btn-ghost px-3 py-2" onClick={() => shift(1)} aria-label="Next month" data-testid="cal-next"><ChevronRight size={18} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] opacity-60 mb-1">{DOW.map(d => <div key={d}>{d}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {cells(ym.y, ym.m).map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const iso = isoOf(ym.y, ym.m, d);
          const wkend = i % 7 >= 5;
          const hol = hols[iso];
          const edge = iso === from || iso === to;
          const inRange = from && to && iso > from && iso < to;
          const cls = edge ? "bg-silver text-black font-bold"
            : inRange ? "bg-neutral-700 text-white"
            : hol ? "bg-amber-900/60 text-amber-200"
            : wkend ? "text-neutral-500" : "text-white";
          return (
            <button type="button" key={iso} title={hol || undefined} onClick={() => tap(iso)} data-testid={`cal-day-${iso}`}
              className={`h-11 rounded text-sm transition-colors ${cls} ${hol ? "ring-1 ring-amber-400/70" : ""}`}>
              {d}{hol && <span className="block w-1 h-1 rounded-full bg-amber-400 mx-auto" />}
            </button>
          );
        })}
      </div>
      <div className="flex gap-3 flex-wrap text-[11px] opacity-70 mt-2">
        <span><span className="inline-block w-2 h-2 bg-amber-400 rounded-full mr-1" />Public holiday</span>
        <span>Weekends and public holidays aren't counted</span>
      </div>
      {tip && <div className="text-xs text-amber-200 mt-1" data-testid="cal-holiday-tip">{tip}</div>}
    </div>
  );
}
